import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'
import useNode from './useNode'
import { supabase } from '../supabaseClient'

// The PENDING upgrade record of each loaded node (app#361): the router offered a version and the
// node has not registered at it yet. The upgrade mark reads it (utilities/nodeVersion.ts).
//
// ⚠ NOT kept live by realtime: `upgrades` is not in the `supabase_realtime` publication, the same
// case as node_metadata (app#132). It is re-fetched where node_metadata is: controller.loadState
// once nodes resolve, useOrganizations.setActiveOrg on an org switch, and Filter's clickHandler.
// A node that enters the node store between two of those gets its own read (`getForNodes`, app#436).
// ⚠ Readable only from router migration 0205 on (router#1625). Before it, the read is refused,
// the store stays empty, and no node is marked behind.

export interface UpgradesState {
  upgrades: UpgradeRecord[]
  get: () => Promise<void>
  getForNodes: (nodeIds: string[]) => Promise<void>
  getByNodeId: (nodeId: string) => UpgradeRecord | undefined
  reset: () => void
}

const state = {
  upgrades: [] as UpgradeRecord[],
}

const FIELDS = 'node_id, to_version, created_at, completed_at, outcome, reason, outcome_at'

const useUpgrades = create<UpgradesState>()((set, get) => ({
  ...state,
  get: async () => {
    const loadedNodeIds = useNode.getState().nodes.map(node => node.id)
    if (!loadedNodeIds.length) return set({ upgrades: [] })
    const asked = new Set(loadedNodeIds)

    const { data, error } = await supabase
      .from('upgrades')
      .select(FIELDS)
      .in('node_id', loadedNodeIds)
      .is('completed_at', null)

    if (error) return console.warn(error)
    // ⚠ The read asked for `loadedNodeIds` only. A node that entered the store after that has its
    // own read (`getForNodes`), and this answer must not remove what that read brought. A row of
    // a node the store no longer holds does not stay.
    const loaded = new Set(useNode.getState().nodes.map(node => node.id))
    const rows = data.map(row => camelcaseKeys(row)) as UpgradeRecord[]
    set({ upgrades: [...rows, ...get().upgrades.filter(u => !asked.has(u.nodeId) && loaded.has(u.nodeId))] })
  },
  // Read the pending upgrades of the given nodes, in one query, and replace them in the store.
  // `get()` reads for the node ids of one moment, so a node that enters the node store later had
  // no record here, and no upgrade mark, until a reload (app#436). `useNode` calls this for such nodes.
  // ⚠ A row of a node that left the store during the read (an org switch) is dropped.
  getForNodes: async nodeIds => {
    if (!nodeIds.length) return
    const asked = new Set(nodeIds)
    const { data, error } = await supabase.from('upgrades').select(FIELDS).in('node_id', nodeIds).is('completed_at', null)

    if (error) return console.warn(error)
    const loaded = new Set(useNode.getState().nodes.map(node => node.id))
    const rows = (data.map(row => camelcaseKeys(row)) as UpgradeRecord[]).filter(u => loaded.has(u.nodeId))
    set({ upgrades: [...get().upgrades.filter(u => !asked.has(u.nodeId)), ...rows] })
  },
  getByNodeId: (nodeId: string) => get().upgrades.find(upgrade => upgrade.nodeId === nodeId),
  reset: () => set({ ...state }),
}))

export default useUpgrades
